import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import Check from '@material-ui/icons/Check';
import extendedFormsStyle from 'assets/jss/material-dashboard-pro-react/views/extendedFormsStyle';
import buttonsStyle from 'assets/jss/material-dashboard-pro-react/views/buttonsStyle';
import CardBody from 'components/Card/CardBody';
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import FormTextInput from 'components/FormTextInput/FormTextInput';
import { ReactPhoneNumberAdapter } from 'components/ReactPhoneNumberAdapter/ReactPhoneNumberAdapter';
import FormControlButtons from 'components/FormControlButtons/FormControlButtons';
import {
  fetchBrand,
  fetchBrands,
  createBrand,
  updateBrand,
} from 'actions/Brands';
import { get, post, put } from 'helpers/apiHelpers';
import { addUserBrand, handleBrandChange } from 'actions/Auth';
import withErrorHandler from 'components/HOC/withErrorHandler';
import SelectInput from 'components/FormSelect/SelectInput';
import { Alert } from '@mui/material';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from 'components/CustomButtons/Button';
import { makeStyles } from '@material-ui/styles';
import classNames from 'classnames';
import CardWrapper from 'components/Card/CardWrapper';
import useGetSalesMarketLang from 'hooks/redux/Brands/useGetSalesMarketLang';
import InfoIconTooltip from 'components/InfoIconTooltip/InfoIconTooltip';
import InfoLink from 'components/InfoLink/InfoLink';
import EXTERNAL_URLS from 'constants/externalUrls';
import { toast } from 'react-toastify';

import { modulesDefaultState } from './consts';

const dayFields = [
  'newOrder',
  'newOrderSubscription',
  'changeDate',
  'changeAddress',
  'changeDiet',
  'buyAddons',
  'paymentDeadline',
  'shouldBeDeliveredIn',
  'shouldBeDeliveredInBlock',
];

const days = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

const dayObject = (curr = {}) => {
  const obj = {};

  dayFields.forEach(field => {
    obj[field] = 0;
  });

  return { ...obj, ...curr };
};

const useStyles = makeStyles(() => ({
  ...extendedFormsStyle,
  ...buttonsStyle,
  dialogContent: { minHeight: '100px' },
  dialogContentParagraph: { fontSize: '14px', marginBottom: '30px' },
  dialogContentInputDisabled: {
    background: 'RGB(224,224,224)',
    color: 'RGB(130,130,130)',
  },
  dialogActions: { display: 'flex', justifyContent: 'end' },
  platformBrandLabel: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '4px',
  },
  platformBrandLabelRow: { display: 'flex', alignItems: 'center' },
  platformBrandLabelText: { marginRight: '0.2em' },
  platformBrandDescription: { fontSize: '14px', color: 'rgba(0,0,0,0.6)' },
}));

const BrandInformation = ({
  isEdit,
  errorHandlerCatch,
  errors,
  errorHandlerResetState,
}) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const selectedBrand = useSelector(({ Auth }) => Auth.selectedBrand);
  const salesMarketLang = useGetSalesMarketLang();

  const { t } = useTranslation();
  const classes = useStyles();
  const [domain, setDomain] = useState([]);
  const [domainToChange, setDomainToChange] = useState({
    oldDomain: '',
    newDomain: '',
  });
  const [isOpen, setIsOpen] = useState(false);

  const [brands, setBrands] = useState([]);
  const [accessKeys, setAccessKeys] = useState([]);

  const [brandConfig, setBrandConfig] = useState({
    name: '',
    shortName: '',
    mainDomain: '',
    email: '',
    phone: '',
    showCalculatorLink: false,
    calculatorLink: '',
    dietPanelAccessKey: null,
    shopPanelAccessKey: null,
  });

  const [copyConfig, setCopyConfig] = useState({
    fromBrand: null,
    offer: true,
    dishes: true,
    zones: true,
    deadlines: true,
  });

  const [multinational, setMultinational] = useState({
    salesMarket: salesMarketLang,
  });

  const [platformBrandConfiguration, setPlatformBrandConfiguration] = useState(
    modulesDefaultState.Foodsi
  );

  useEffect(() => {
    let isMounted = true;

    (async () => {
      try {
        const [
          brandsResponse,
          accessKeysResponse,
          domainsResponse,
          brandResponse,
          modulesResponse,
        ] = await Promise.all([
          dispatch(fetchBrands()),
          get('/brand-access-keys'),
          get('/panel-domains'),
          isEdit ? dispatch(fetchBrand(selectedBrand)) : Promise.resolve(null),
          isEdit
            ? get(`brands/${selectedBrand}/modules`, {
                'module[]': 'Foodsi',
              })
            : Promise.resolve(null),
        ]);

        if (!isMounted) {
          return;
        }

        setBrands(brandsResponse);
        const formattedAccessKeys = accessKeysResponse['hydra:member'].map(
          key => ({
            label: key.name,
            value: key['@id'],
            id: key.id,
          })
        );
        setAccessKeys([
          {
            label: t('brandCfg.accessKeyDietPanel.cleanup'),
            value: '',
            id: null,
          },
          ...formattedAccessKeys,
        ]);

        if (isEdit) {
          const {
            name,
            shortName,
            mainDomain,
            email,
            phone,
            showCalculatorLink,
            calculatorLink,
            dietPanelAccessKey,
            shopPanelAccessKey,
            multinational: fetchedMultinational,
          } = brandResponse;

          setBrandConfig({
            name,
            shortName,
            mainDomain,
            email,
            phone,
            showCalculatorLink,
            calculatorLink,
            dietPanelAccessKey,
            shopPanelAccessKey,
          });

          setMultinational(fetchedMultinational);
          setPlatformBrandConfiguration(modulesResponse.configuration.Foodsi);
        }

        const formattedDomains = domainsResponse.domains.map(
          (domainItem, index) => ({
            key: index,
            label: domainItem,
            value: domainItem,
          })
        );
        setDomain(formattedDomains);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading brand data:', error);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [isEdit, selectedBrand, dispatch]);

  const handleInputChange = e => {
    setBrandConfig(prevState => ({
      ...prevState,
      [e.target.name]: e.target.value,
    }));
  };

  const handlePhoneChange = (number, countryCode) => {
    setBrandConfig(prevState => ({
      ...prevState,
      phone: {
        ...prevState.phone,
        number,
        countryCode,
      },
    }));
  };

  const brandAction = data =>
    isEdit
      ? dispatch(updateBrand(selectedBrand, data))
      : dispatch(createBrand(data));

  const platformBrandAction = brandId =>
    put(`/brands/${brandId}/modules`, {
      module: 'Foodsi',
      configuration: {
        Foodsi: platformBrandConfiguration,
      },
    });

  const handleSubmit = async () => {
    const {
      name,
      shortName,
      mainDomain,
      email,
      phone,
      showCalculatorLink,
      calculatorLink,
      dietPanelAccessKey,
      shopPanelAccessKey,
    } = brandConfig;

    const data = {
      name,
      shortName,
      mainDomain,
      email,
      phone,
      showCalculatorLink,
      calculatorLink,
      dietPanelAccessKey,
      shopPanelAccessKey,
      multinational,
      copy:
        copyConfig.fromBrand?.['@id'] ?? copyConfig.fromBrand
          ? {
              fromBrand: copyConfig.fromBrand['@id'] ?? copyConfig.fromBrand,
              offer: copyConfig.offer,
              dishes: copyConfig.dishes && copyConfig.offer,
              zones: copyConfig.zones,
              deadlines: copyConfig.deadlines,
            }
          : null,
    };

    if (!isEdit) {
      days.forEach(field => {
        data[field] = dayObject();
      });

      data.adminDays = [];
      data.days = [];
      data.daysDefault = null;
      data.daysMax = null;
      data.daysMin = null;
      data.daysSelectorType = 'RANGE';
    }

    try {
      errorHandlerResetState();

      const brandResponse = await brandAction(data);

      platformBrandAction(brandResponse.id);
      if (isEdit) {
        toast.success(t('success.changesSaved'));
      } else {
        toast.success(
          `${t('brandInformation.createBrand')} ${t(
            'brandInformation.reminderRolesInfo'
          )}`
        );

        dispatch(
          handleBrandChange(null, { brand: brandResponse, refresh: false })
        );
        history.push(
          `/admin/client-panel-design?selectedBrand=${brandResponse.id}`
        );
      }

      await dispatch(
        addUserBrand({
          '@id': brandResponse['@id'],
          id: brandResponse.id,
          name: brandResponse.name,
        })
      );

      if (!isEdit) {
        history.push('/admin/company');
      }
    } catch (error) {
      errorHandlerCatch(error);
    }
  };

  const saveNewLinkDomain = async () => {
    try {
      await post('/panel-domains-update', {
        oldDomain: domainToChange.oldDomain,
        newDomain: domainToChange.newDomain,
      });
      toast.success(t('success.changesSaved.changedDomain'));
    } catch {
      toast.error(t('error.changesSaved.changedDomain'));
    } finally {
      setIsOpen(false);
    }
  };

  const handleSalesMarketChange = salesMarket => {
    setMultinational(prevState => ({
      ...prevState,
      salesMarket,
    }));
  };

  const toggleEnabled = () => {
    setPlatformBrandConfiguration(prev => ({
      ...prev,
      enabled: !prev.enabled,
    }));
  };

  useEffect(() => {
    handleSalesMarketChange(salesMarketLang);
  }, [salesMarketLang]);

  return (
    <>
      <CardWrapper title={t('brandCfg.basicInformations')}>
        <CardBody>
          <GridContainer>
            <GridItem md={6}>
              <FormTextInput
                label={`${t('brandCfg.brandName')}*`}
                classes={classes}
                name="name"
                value={brandConfig.name}
                handleChange={handleInputChange}
                inputSize={12}
                errors={errors}
              />
              <FormTextInput
                label={t('brandCfg.mainDomain')}
                classes={classes}
                name="mainDomain"
                value={brandConfig.mainDomain}
                maxLength={64}
                handleChange={handleInputChange}
                inputSize={12}
              />
              <SelectInput
                label={t('brandCfg.mainDomain.impresonationMode')}
                classes={classes}
                mapBy="label"
                trackBy="label"
                value={domainToChange.oldDomain}
                options={domain}
                handleChange={(ev, obj) => {
                  setDomainToChange(prev => ({
                    ...prev,
                    oldDomain: obj.value,
                    ...(domainToChange.newDomain === '' && {
                      newDomain: brandConfig.mainDomain,
                    }),
                  }));
                  setIsOpen(true);
                }}
                size={12}
              />

              <FormTextInput
                label={t('brandCfg.short')}
                classes={classes}
                name="shortName"
                value={brandConfig.shortName}
                handleChange={handleInputChange}
                inputSize={12}
              />
              <SelectInput
                label={t('brandCfg.accessKeyDietPanel')}
                classes={classes}
                mapBy="label"
                trackBy="value"
                value={brandConfig.dietPanelAccessKey}
                options={accessKeys}
                handleChange={(ev, obj) => {
                  setBrandConfig(prevState => ({
                    ...prevState,
                    dietPanelAccessKey: obj?.value || null,
                  }));
                }}
                size={12}
              />
              <SelectInput
                label={t('brandCfg.accessKeyShopPanel')}
                classes={classes}
                mapBy="label"
                trackBy="value"
                value={brandConfig.shopPanelAccessKey}
                options={accessKeys}
                handleChange={(ev, obj) => {
                  setBrandConfig(prevState => ({
                    ...prevState,
                    shopPanelAccessKey: obj?.value || null,
                  }));
                }}
                size={12}
              />
            </GridItem>
            <GridItem md={6}>
              <FormTextInput
                label={t('brandCfg.emailAddr')}
                classes={classes}
                name="email"
                value={brandConfig.email}
                handleChange={handleInputChange}
                inputSize={12}
                errors={errors}
              />
              <ReactPhoneNumberAdapter
                label={t('brandCfg.infolineClient')}
                name="phone"
                value={brandConfig.phone}
                onChange={handlePhoneChange}
                inputSize={12}
              />
            </GridItem>
          </GridContainer>
        </CardBody>
        {!isEdit && (
          <CardBody>
            <GridContainer>
              <GridItem md={12}>
                <h1>{t('brandCfg.copy')}</h1>
              </GridItem>
              <GridItem md={12}>
                <SelectInput
                  label={t('brandCfg.selectBrand')}
                  classes={classes}
                  mapBy="name"
                  trackBy="@id"
                  value={copyConfig.fromBrand}
                  options={brands}
                  handleChange={(ev, obj) => {
                    setCopyConfig(prevState => ({
                      ...prevState,
                      fromBrand:
                        copyConfig.fromBrand === obj['@id'] ? null : obj['@id'],
                    }));
                  }}
                  size={12}
                />
              </GridItem>
              {copyConfig.fromBrand && (
                <>
                  <GridItem md={12}>
                    <Alert severity="info">{t('brandCfg.copy.info')}</Alert>
                  </GridItem>
                  <GridItem md={copyConfig.offer ? 2 : 12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={copyConfig.offer}
                          onChange={() => {
                            setCopyConfig(prevState => ({
                              ...prevState,
                              offer: !copyConfig.offer,
                            }));
                          }}
                          checkedIcon={
                            <Check className={classes.checkedIcon} />
                          }
                          icon={<Check className={classes.uncheckedIcon} />}
                          classes={{
                            checked: classes.checked,
                            root: classes.checkRoot,
                          }}
                        />
                      }
                      label={t('brandCfg.copy.offer')}
                    />
                  </GridItem>
                  {copyConfig.offer && (
                    <GridItem md={10}>
                      <Alert severity="info">
                        {t('brandCfg.copy.offer.info')}
                      </Alert>
                    </GridItem>
                  )}
                  <GridItem md={copyConfig.dishes && copyConfig.offer ? 2 : 12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          disabled={!copyConfig.offer}
                          checked={copyConfig.dishes && copyConfig.offer}
                          onChange={() => {
                            setCopyConfig(prevState => ({
                              ...prevState,
                              dishes: !copyConfig.dishes,
                            }));
                          }}
                          checkedIcon={
                            <Check className={classes.checkedIcon} />
                          }
                          icon={<Check className={classes.uncheckedIcon} />}
                          classes={{
                            checked: classes.checked,
                            root: classes.checkRoot,
                          }}
                        />
                      }
                      label={t('brandCfg.copy.dishes')}
                    />
                  </GridItem>
                  {copyConfig.dishes && copyConfig.offer && (
                    <GridItem md={10}>
                      <Alert severity="info">
                        {t('brandCfg.copy.offer.info2')}
                      </Alert>
                    </GridItem>
                  )}
                  <GridItem md={copyConfig.deadlines ? 2 : 12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={copyConfig.deadlines}
                          onChange={() => {
                            setCopyConfig(prevState => ({
                              ...prevState,
                              deadlines: !copyConfig.deadlines,
                            }));
                          }}
                          checkedIcon={
                            <Check className={classes.checkedIcon} />
                          }
                          icon={<Check className={classes.uncheckedIcon} />}
                          classes={{
                            checked: classes.checked,
                            root: classes.checkRoot,
                          }}
                        />
                      }
                      label={t('brandCfg.copy.deadlines')}
                    />
                  </GridItem>
                  {copyConfig.deadlines && (
                    <GridItem md={10}>
                      <Alert severity="info">
                        {t('brandCfg.copy.deadlines.info')}
                      </Alert>
                    </GridItem>
                  )}
                </>
              )}
            </GridContainer>
          </CardBody>
        )}
      </CardWrapper>

      <CardWrapper title={t('brandCfg.additionalSettings')}>
        <CardBody>
          <GridContainer>
            <GridItem md={6}>
              <SelectInput
                label={t('brandCfg.additionalSettings.salesMarket')}
                classes={classes}
                mapBy="label"
                trackBy="value"
                name="salesMarket"
                value={multinational.salesMarket}
                options={[
                  {
                    label: t('brandCfg.additionalSettings.salesMarket.poland'),
                    value: 'pl',
                  },
                  {
                    label: t(
                      'brandCfg.additionalSettings.salesMarket.czechRepublic'
                    ),
                    value: 'cs',
                  },
                  {
                    label: t('brandCfg.additionalSettings.salesMarket.latvia'),
                    value: 'lv',
                  },
                ]}
                handleChange={({ target: { value } }) => {
                  handleSalesMarketChange(value);
                }}
                size={12}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={platformBrandConfiguration.enabled}
                    onClick={toggleEnabled}
                    checkedIcon={<Check className={classes.checkedIcon} />}
                    icon={<Check className={classes.uncheckedIcon} />}
                    classes={{
                      checked: classes.checked,
                      root: classes.checkRoot,
                    }}
                  />
                }
                label={
                  <div className={classes.platformBrandLabel}>
                    <div className={classes.platformBrandLabelRow}>
                      <span className={classes.platformBrandLabelText}>
                        {t('brandCfg.additionalSettings.platformBrand')}
                      </span>
                      <InfoIconTooltip
                        title={t(
                          'brandCfg.additionalSettings.platformBrandInfo'
                        )}
                      />
                    </div>
                    <span className={classes.platformBrandDescription}>
                      {t(
                        'brandCfg.additionalSettings.platformBrandDescription'
                      )}
                    </span>
                    <InfoLink link={EXTERNAL_URLS.platformBrand} />
                  </div>
                }
              />
            </GridItem>
          </GridContainer>
        </CardBody>
      </CardWrapper>

      <FormControlButtons
        classes={classes}
        submitText={t('common.shared.save')}
        handleSubmit={handleSubmit}
      />

      <Dialog
        maxWidth="sm"
        open={isOpen}
        onClose={() => {
          setIsOpen(false);
        }}
      >
        <DialogTitle className={classes.modalHeader}>
          {t('brandCfg.impresonationMode.modalTitle')}
        </DialogTitle>
        <DialogContent
          className={classNames(classes.modalBody, classes.dialogContent)}
        >
          <p className={classes.dialogContentParagraph}>
            {t('brandCfg.impresonationMode.modalDescription')}
          </p>

          <FormTextInput
            className={classes.dialogContentInputDisabled}
            label={t('brandCfg.oldDomain')}
            classes={classes}
            name="oldDomain"
            value={domainToChange.oldDomain}
            disabled={true}
            inputSize={12}
            errors={errors}
          />

          <FormTextInput
            label={t('brandCfg.newDomain')}
            classes={classes}
            name="newDomain"
            value={domainToChange.newDomain}
            handleChange={e => {
              setDomainToChange(prev => ({
                ...prev,
                newDomain: e.target.value,
              }));
            }}
            inputSize={12}
            errors={errors}
          />
        </DialogContent>
        <DialogActions
          className={classNames(classes.modalFooter, classes.dialogActions)}
        >
          <Button
            color="danger"
            onClick={() => {
              setIsOpen(false);
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button onClick={saveNewLinkDomain} color="success">
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default withErrorHandler(BrandInformation);
